import { useState, type ReactNode } from "react";
import { Check, Copy, Play } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { Badge, Button, EmptyState, cn } from "@fox1ck-org/ui";
import { CostModel } from "@gen/tracker/v1/campaign_pb";

import { CardKpis } from "@/components/campaign/kpis";
import { Aside, Panel } from "@/components/campaign/panel";
import { useCampaignNetworks, useLastPostback } from "@/components/campaign/partners";
import { useT } from "@/lib/i18n";
import { formatAge } from "@/lib/format";
import { formatStamp } from "@/lib/utils";
import { useCampaignCard, useRenderAdLink } from "@/lib/services/campaign-service";
import { newNonce, readNonce, testLink, useTestClick } from "@/lib/services/test-postback-service";
import { useReportTimezone } from "@/lib/services/workspace-service";
import type { CardContext } from "./card";

/**
 * Новая кампания без трафика (доска CEmpty): the strip in dashes, «Ждём
 * первый клик» with the link to copy and the test click, and «Готовность» —
 * what is done and what is left before the link goes into the ads. Every
 * line is a fact the server holds: the campaign row, the domain binding, the
 * partner's postbacks, the buyer's own test click. The summary swaps this for
 * the numbers by itself once the first visit lands (it polls).
 */
export function EmptyCampaign({ ctx, lifeRange }: { ctx: CardContext; lifeRange: { from: string; to: string; tz: string } }) {
  const t = useT();
  const tz = useReportTimezone();
  const c = ctx.campaign;
  // The summary's own lifetime query, polled while this screen is up: the
  // first visit turns the card into numbers without a reload.
  useCampaignCard(c.id, lifeRange, undefined, { refetchInterval: 15_000 });
  const ad = useRenderAdLink(c.id);
  const link = ad.data?.link?.link ?? "";
  const [nonce, setNonce] = useState(() => readNonce(c.id));
  const test = useTestClick(c.id, nonce);
  const { networks } = useCampaignNetworks(c, ctx.item);
  const network = networks[0];
  const source = ctx.item?.trafficSourceName || t("links.source");

  const copy = () => {
    if (!link) return;
    void navigator.clipboard.writeText(link).then(() => toast.success(t("card.toast.linkCopied")));
  };
  const openTest = () => {
    if (!link) return;
    const n = newNonce(c.id);
    setNonce(n);
    window.open(testLink(link, n), "_blank", "noopener");
  };

  const domainOk = !!ctx.item?.domainServes;
  const lastPb = useLastPostback(network?.id ?? "", c.id);
  const items: { done: boolean; title: string; note: ReactNode; action?: ReactNode }[] = [
    {
      done: true,
      title: t("card.ready.created"),
      note: [formatStamp(c.createdAt, tz, { clock: true }), ctx.item?.ownerName].filter(Boolean).join(" · "),
    },
    {
      done: domainOk,
      title: ctx.item?.fqdn ? t("card.ready.domain", { fqdn: ctx.item.fqdn }) : t("card.ready.domain.none"),
      note: domainOk ? t("card.ready.domain.ok") : t("card.ready.domain.wait"),
    },
    network
      ? { done: !!lastPb.data?.items[0], title: t("card.ready.postback"), note: <PartnerNote networkId={network.id} name={network.name} campaignId={c.id} /> }
      : { done: false, title: t("card.ready.postback"), note: t("card.ready.postback.none") },
    {
      done: !!test.data?.found,
      title: t("card.ready.test"),
      note: test.data?.found ? t("card.ready.test.done", { ago: formatAge((Date.now() - Date.parse(test.data.clickedAt)) / 1000) }) : t("card.ready.test.why"),
      action: test.data?.found ? undefined : (
        <Button size="sm" onClick={openTest} disabled={!link}>
          {t("card.ready.test.go")}
        </Button>
      ),
    },
    {
      done: false,
      title: t("card.ready.ads", { source }),
      note: t("card.ready.ads.why"),
      action: (
        <Button size="sm" variant="secondary" onClick={copy} disabled={!link}>
          {t("card.ready.ads.copy")}
        </Button>
      ),
    },
  ];
  const done = items.filter((i) => i.done).length;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      <CardKpis v={undefined} prev={undefined} loading={false} empty />
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 xl:grid-cols-2">
        <section className="grid min-h-80 place-items-center rounded-lg border border-border bg-card p-6">
          <EmptyState
            icon={<Play className="size-5 text-primary" strokeWidth={1.5} />}
            title={t("card.empty.title")}
            description={
              <span className="flex flex-col gap-3">
                <span>{t("card.empty.body")}</span>
                {c.costModel === CostModel.AUTO ? <span className="text-xs">{t("card.empty.cost", { source })}</span> : null}
              </span>
            }
            action={
              <span className="flex flex-wrap justify-center gap-2">
                <Button onClick={copy} disabled={!link}>
                  <Copy className="size-4" /> {t("card.ready.ads.copy")}
                </Button>
                <Button variant="secondary" onClick={openTest} disabled={!link}>
                  <Play className="size-4" /> {t("card.empty.test")}
                </Button>
              </span>
            }
          />
        </section>
        <Panel
          title={t("card.ready.title")}
          aside={
            <Badge variant={done === items.length ? "ok" : "warn"}>
              {t("card.ready.count", { done, total: items.length })}
            </Badge>
          }
        >
          <ul className="flex flex-col">
            {items.map((i) => (
              <li key={i.title} className="flex items-center gap-3 border-b border-border px-3.5 py-3 last:border-b-0">
                <span
                  aria-hidden
                  className={cn(
                    "grid size-5 shrink-0 place-items-center rounded-full border",
                    i.done ? "border-ok-line bg-ok-soft text-ok" : "border-border-strong text-transparent",
                  )}
                >
                  <Check className="size-3" strokeWidth={2.5} />
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-sm font-medium">{i.title}</span>
                  <span className="text-xs text-muted-foreground">{i.note}</span>
                </div>
                {i.action}
                <span className="sr-only">{i.done ? t("card.ready.done") : t("card.ready.todo")}</span>
              </li>
            ))}
          </ul>
          <div className="mt-auto border-t border-border px-3.5 py-2">
            <Aside>{t("card.ready.hint")}</Aside>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function PartnerNote({ networkId, name, campaignId }: { networkId: string; name: string; campaignId: string }) {
  const t = useT();
  const q = useLastPostback(networkId, campaignId);
  const at = q.data?.items[0]?.receivedAt;
  if (q.isLoading) return <>…</>;
  return <>{at ? t("card.ready.postback.seen", { network: name, ago: formatAge((Date.now() - Date.parse(at)) / 1000) }) : t("card.ready.postback.wait", { network: name })}</>;
}
